import { useTitle } from "@/hooks/use-title.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { WorkflowFolderCreateModal } from "@/pages/workflows/edit/components/WorkflowFolderCreateModal.tsx";
import { WorkflowEditSidebar } from "@/pages/workflows/edit/components/WorkflowEditSidebar.tsx";
import { WorkflowEditPageStep } from "@/pages/workflows/edit/pages/workflow-edit-pages.tsx";
import { buildWorkflowEditDetailsSchema } from "@/pages/workflows/edit/pages/workflow-edit-details.schema.ts";
import { useWorkflowFolders } from "@/queries/workflows/workflow-folder.query.ts";
import { useWorkflowCreate } from "@/queries/workflows/workflow.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { ApiWorkflowCreate } from "@/types/workflows/workflow.types.ts";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  FullScreenPage,
  Input,
  Select,
  Stack,
  Textarea,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

export const WorkflowNewPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { canIEdit } = usePermissions();
  const { openModal } = useModals();

  useTitle(t("workflow:create_new"), t("workflow:workflows"));

  const workflowCreate = useWorkflowCreate();
  const { data: workflowFolders, isLoading: isWorkflowFoldersLoading } = useWorkflowFolders();
  const workflowFolderOptions = useMemo(() => workflowFolders ?? [], [workflowFolders]);
  const canCreateWorkflowFolders = canIEdit(ApiPermission.workflows_manage_all);
  const initialFolderId = useMemo(() => {
    const folderId = searchParams.get("folder_id");
    if (!folderId) return undefined;
    const parsedFolderId = Number(folderId);
    return Number.isNaN(parsedFolderId) ? undefined : parsedFolderId;
  }, [searchParams]);

  const workflowEditDetailsSchema = buildWorkflowEditDetailsSchema();

  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ApiWorkflowCreate>({
    mode: "onSubmit",
    resolver: yupResolver(workflowEditDetailsSchema),
    defaultValues: {
      folder_id: initialFolderId,
    },
  });

  const handleSave = handleSubmit(async (data) => {
    try {
      const result = await workflowCreate.mutateAsync({
        payload: { ...data },
      });

      analyticsService.trackEvent(analyticEvents.workflows.created, {
        [analyticProperties.id]: result.id,
      });

      navigate(`/workflows/${result.id}/setup/${WorkflowEditPageStep.visibility}`);
    } catch {
      //  handle error
    }
  });

  return (
    <FullScreenPage
      isCenterVertical={false}
      title={t("workflow:create_new")}
      backSlot={
        <Button variant={"secondary"} size={"lg"} onClick={() => navigate(`/workflows/library`)}>
          {t("common:exit")}
        </Button>
      }
      actionsSlot={
        <Button size={"lg"} onClick={handleSave} isLoading={workflowCreate.isPending}>
          {t("common:continue")}
        </Button>
      }
      sidebarSlot={<WorkflowEditSidebar step={WorkflowEditPageStep.details} />}
    >
      <form onSubmit={handleSave}>
        <Stack gap={"sm"} justify={"center"}>
          <Stack className={"card-panel"} gap={"2xl"}>
            <Stack gap={"lg"}>
              <Input
                type={"text"}
                label={t("common:title")}
                required={true}
                size={"lg"}
                {...register("title")}
                error={errors.title?.message}
              />
              <Textarea
                label={t("common:description")}
                {...register("description")}
                error={errors.description?.message}
              />
            </Stack>
          </Stack>
          <Stack className={"card-panel"} gap={"lg"}>
            <Title header={t("workflow:category")} size={"md"} />
            <Controller
              render={({ field: { value, onChange } }) => (
                <Select
                  label={t("workflow:category")}
                  emptyTitle={t("common:select")}
                  value={value}
                  options={workflowFolderOptions}
                  onChange={(nextValue) => {
                    onChange(nextValue ?? null);
                  }}
                  error={errors.folder_id?.message}
                  isLoading={isWorkflowFoldersLoading}
                  required={true}
                  searchable={true}
                  onAddNew={
                    canCreateWorkflowFolders
                      ? (searchText) => {
                          openModal(WorkflowFolderCreateModal, {
                            initialTitle: searchText,
                            onSaved: async (folder) => {
                              setValue("folder_id", folder.id, { shouldValidate: true });
                            },
                          });
                        }
                      : undefined
                  }
                />
              )}
              control={control}
              name={"folder_id"}
            />
          </Stack>

          <Button type={"submit"} size={"lg"} isLoading={workflowCreate.isPending}>
            {t("common:continue")}
          </Button>
        </Stack>
      </form>
    </FullScreenPage>
  );
};
